import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, PriceInput, Select, Textarea } from '@artosera/ui';
import {
  DEVISES,
  TYPES_ESTIMATION,
  formatDate,
  type AuteurEstimation,
  type Estimation,
} from '@artosera/shared';
import {
  useAssuranceOeuvre,
  useCreerEstimation,
  useAnnulerEstimation,
  messageErreurAssurance,
  type EstimationPresentee,
} from '../../../api/assurance.js';
import { Field } from '../../Field.js';
import { AuteurEstimationChamps } from '../../assurance/AuteurEstimationChamps.js';
import styles from '../../assurance/AssurancePage.module.css';

export function AssuranceTab({
  galerieSlug,
  oeuvreId,
  compteId,
  peutEcrire,
}: {
  galerieSlug: string;
  oeuvreId: string;
  compteId: string;
  peutEcrire: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const fiche = useAssuranceOeuvre(galerieSlug, oeuvreId);
  const creer = useCreerEstimation(galerieSlug, oeuvreId);
  const annuler = useAnnulerEstimation(galerieSlug);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [nouvelle, setNouvelle] = useState(false);
  const [originale, setOriginale] = useState<EstimationPresentee | null>(null);
  const [montant, setMontant] = useState<number | null>(null);
  const [devise, setDevise] = useState<Estimation['devise']>('EUR');
  const [type, setType] = useState<Estimation['type']>('assurance');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [auteur, setAuteur] = useState<AuteurEstimation>({ type: 'membre', compteId });
  const [motif, setMotif] = useState('');
  const [justification, setJustification] = useState('');
  const [erreur, setErreur] = useState('');
  async function soumettre() {
    setErreur('');
    try {
      if (originale)
        await annuler.mutateAsync({
          id: originale._id,
          motif,
          ...(montant === null ? {} : { montantCentimes: montant }),
        });
      else if (montant !== null)
        await creer.mutateAsync({
          type,
          montantCentimes: montant,
          devise,
          dateEffet: new Date(date).toISOString(),
          auteur,
          justification,
          mediaIds: [],
          documentIds: [],
        });
      setNouvelle(false);
      setOriginale(null);
    } catch (e) {
      setErreur(messageErreurAssurance(e));
    }
  }
  if (fiche.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (fiche.isError) return <p role="alert">{messageErreurAssurance(fiche.error)}</p>;
  const donnees = fiche.data;
  const valeur = donnees.valeurCourante;
  return (
    <div className={styles.liste}>
      <section className={styles.carte}>
        <h2>{t('regie:assurance.valeurCourante')}</h2>
        {valeur ? (
          <p>
            <Price cents={valeur.montantCentimes!} currency={valeur.devise} lang={langue} /> —{' '}
            {formatDate(new Date(valeur.dateEffet), langue, 'court')}
          </p>
        ) : (
          <p>{t('regie:assurance.sansValeur')}</p>
        )}
        {donnees.exemplaires.map((e) => (
          <p key={e.exemplaireId}>
            {t('regie:assurance.exemplaire', { id: e.exemplaireId })} :{' '}
            {e.valeurCourante ? (
              <>
                <Price
                  cents={e.valeurCourante.montantCentimes!}
                  currency={e.valeurCourante.devise}
                  lang={langue}
                />{' '}
                — {formatDate(new Date(e.valeurCourante.dateEffet), langue, 'court')}
              </>
            ) : (
              t('regie:assurance.sansValeur')
            )}
          </p>
        ))}
        {peutEcrire && (
          <Button
            onClick={() => {
              setMontant(donnees.suggestion?.montantCentimes ?? null);
              setDevise(donnees.suggestion?.devise ?? 'EUR');
              setType('assurance');
              setErreur('');
              setNouvelle(true);
            }}
          >
            {t('regie:assurance.nouvelleEstimation')}
          </Button>
        )}
      </section>
      <section className={styles.carte}>
        <h2>{t('regie:assurance.couverture')}</h2>
        {donnees.couverture.length === 0 && <p>{t('regie:assurance.sansCouverture')}</p>}
        {donnees.couverture.map((c) => (
          <p key={c.police._id}>
            {c.police.numero} — {t(`regie:assurance.couvertures.${c.raison}`)}
          </p>
        ))}
      </section>
      <section className={styles.liste}>
        <h2>{t('regie:assurance.historique')}</h2>
        {donnees.historique.length === 0 && <p>{t('regie:assurance.vide')}</p>}
        {donnees.historique.map((e) => {
          const annulation = donnees.historique.find((a) => a.annuleId === e._id);
          const nomAuteur = e.auteur.type === 'membre' ? e.auteur.compteId : e.auteur.nom;
          return (
            <article key={e._id} id={`estimation-${e._id}`} className={styles.carte}>
              <p>
                {t(`regie:assurance.typesEstimation.${e.type}`)} —{' '}
                {e.montantCentimes === undefined ? (
                  t('regie:assurance.annulation')
                ) : (
                  <Price cents={e.montantCentimes} currency={e.devise} lang={langue} />
                )}{' '}
                — {formatDate(new Date(e.dateEffet), langue, 'court')}
              </p>
              <p>
                {t(`regie:assurance.auteurs.${e.auteur.type}`)} : {nomAuteur} —{' '}
                {t('regie:assurance.creeeLe', {
                  date: formatDate(new Date(e.creeLe), langue, 'court'),
                })}
              </p>
              {e.exemplaireId && <p>{t('regie:assurance.exemplaire', { id: e.exemplaireId })}</p>}
              {e.methode && <p>{e.methode}</p>}
              {e.justification && <p>{e.justification}</p>}
              {e.motif && (
                <p>
                  {t('regie:assurance.motif')} : {e.motif}
                </p>
              )}
              {e.annuleId && (
                <a href={`#estimation-${e.annuleId}`}>
                  {t('regie:assurance.remplace', { id: e.annuleId })}
                </a>
              )}
              {annulation && (
                <a href={`#estimation-${annulation._id}`}>
                  {t('regie:assurance.annuleePar', { id: annulation._id })}
                </a>
              )}
              {peutEcrire && e.montantCentimes !== undefined && !annulation && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOriginale(e);
                    setMontant(null);
                    setMotif('');
                    setErreur('');
                  }}
                >
                  {t('regie:assurance.corrigerAnnuler')}
                </Button>
              )}
            </article>
          );
        })}
      </section>
      {(nouvelle || originale) && peutEcrire && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) {
              setNouvelle(false);
              setOriginale(null);
            }
          }}
          title={t(
            originale ? 'regie:assurance.corrigerAnnuler' : 'regie:assurance.nouvelleEstimation',
          )}
          closeLabel={t('commun:actions.fermer')}
        >
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              void soumettre();
            }}
          >
            {nouvelle && donnees.suggestion && <p>{t('regie:assurance.proposition')}</p>}
            <Field htmlFor="estimation-montant" label={t('regie:assurance.montant')}>
              <PriceInput
                optional
                id="estimation-montant"
                currency={originale?.devise ?? devise}
                lang={langue}
                cents={montant}
                onCentsChange={setMontant}
              />
            </Field>
            {originale ? (
              <>
                <p>{t('regie:assurance.annulationAide')}</p>
                <Field htmlFor="estimation-motif" label={t('regie:assurance.motif')}>
                  <Textarea
                    id="estimation-motif"
                    required
                    maxLength={2000}
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field htmlFor="estimation-type" label={t('regie:assurance.typeEstimation')}>
                  <Select
                    id="estimation-type"
                    value={type}
                    onValueChange={(v) => setType(v as Estimation['type'])}
                    options={TYPES_ESTIMATION.map((value) => ({
                      value,
                      label: t(`regie:assurance.typesEstimation.${value}`),
                    }))}
                  />
                </Field>
                <Field htmlFor="estimation-devise" label={t('regie:assurance.devise')}>
                  <Select
                    id="estimation-devise"
                    value={devise}
                    onValueChange={(v) => setDevise(v as Estimation['devise'])}
                    options={DEVISES.map((value) => ({ value, label: value }))}
                  />
                </Field>
                <Field htmlFor="estimation-date" label={t('regie:assurance.dateEffet')}>
                  <Input
                    id="estimation-date"
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Field>
                <AuteurEstimationChamps compteId={compteId} auteur={auteur} onChange={setAuteur} />
                <Field
                  htmlFor="estimation-justification"
                  label={t('regie:assurance.justification')}
                >
                  <Textarea
                    id="estimation-justification"
                    maxLength={5000}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                  />
                </Field>
              </>
            )}
            {erreur && <p role="alert">{erreur}</p>}
            <Button
              type="submit"
              loading={creer.isPending || annuler.isPending}
              disabled={originale ? !motif.trim() : montant === null}
            >
              {t('commun:actions.enregistrer')}
            </Button>
          </form>
        </Dialog>
      )}
    </div>
  );
}
